import { Dispatch, RefObject, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { FieldValues, UseFormGetValues, UseFormSetValue } from "react-hook-form";

import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import { getEquipos } from "@/services/aux-entities.service";
import {
  ActiveDirectoryDriverUser,
  getDriversActiveDirectoryUsers,
} from "@/services/drivers-ad.service";
import {
  createDriver,
  CreateDriverProps,
  deleteDrivers,
  getDriver,
  getDriversList,
  GetDriversListProps,
  updateDriver,
  UpdateDriverProps,
} from "@/services/drivers.service";
import { getTrucksList } from "@/services/trucks.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { handlePermissionError } from "@/utils/permission-check";
import { isEmpty } from "@/utils/query-string";
import { Option } from "@/components/select/AsyncAutocompleteSelect";

import { DLFRProps, getDriversFilter } from "./filter-obj";
import { DriversListResponse, EquipoEntity } from "./drivers.schema";

interface FetchDriversHandlerProps {
  setLoadingDrivers: Dispatch<SetStateAction<boolean>>;
  setDrivers: Dispatch<SetStateAction<DriversListResponse | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  showLoad?: boolean;
  filters?: Partial<GetDriversListProps>;
}

export const fetchDriversHandler = async ({
  setLoadingDrivers,
  setDrivers,
  navigate,
  fetchPermissions,
  showLoad = true,
  filters,
}: FetchDriversHandlerProps) => {
  try {
    if (showLoad) setLoadingDrivers(true);

    const request: GetDriversListProps = {
      equipos: ((filters?.equipos ?? []) as any[])
        .map((x) => (typeof x === "number" ? x : Number(x)))
        .filter((x) => Number.isFinite(x)),
      "P.Pagina": (filters as any)?.["P.Pagina"] ?? 1,
      "P.Records": (filters as any)?.["P.Records"] ?? 10,
    };

    if (filters?.nombreApellido) request.nombreApellido = filters.nombreApellido;
    if (typeof filters?.activo === "boolean") request.activo = filters.activo;

    const response = await getDriversList(request);
    setDrivers(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.DRIVERS,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el listado de fleteros.",
      });
    }
  } finally {
    if (showLoad) setLoadingDrivers(false);
  }
};

interface FetchDriverHandlerProps {
  id: number;
  setLoadingDriver: Dispatch<SetStateAction<boolean>>;
  setValue: UseFormSetValue<any>;
  replaceTrucks: (value: { equipoId?: unknown }[]) => void;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchDriverHandler = async ({
  id,
  setLoadingDriver,
  setValue,
  replaceTrucks,
  navigate,
  fetchPermissions,
}: FetchDriverHandlerProps) => {
  try {
    setLoadingDriver(true);
    const driver = await getDriver({ id });

    if (!driver) return;

    const usuarioValue = driver?.usuario;
    if (usuarioValue) {
      setValue("usuario", {
        label: `${driver.nombreApellido} - ${driver.email}`,
        value: driver?.usuario,
      });
    }

    setValue("nombreApellido", driver.nombreApellido ?? "");
    setValue("activo", [Boolean(driver.activo)]);

    const rawEquipos: EquipoEntity[] = Array.isArray(driver.equipos) ? driver.equipos : [];
    const equiposIds = rawEquipos
      .map((e) => Number(e.idEquipo))
      .filter((x: number) => Number.isFinite(x));

    replaceTrucks(
      (equiposIds.length ? equiposIds : [undefined]).map(
        (eq: number | undefined) => ({
          equipoId: eq === undefined ? [] : [String(eq)],
        }),
      ),
    );
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.DRIVER_EDIT,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo cargar el fletero.",
      });
    }
  } finally {
    setLoadingDriver(false);
  }
};

export interface DriverUpdateFormValues {
  activo?: unknown;
  equipos?: { equipoId?: unknown }[];
}

interface SubmitUpdateDriverHandlerProps {
  id: number;
  values: DriverUpdateFormValues;
  setLoadingSave: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitUpdateDriverHandler = async ({
  id,
  values,
  setLoadingSave,
  navigate,
  fetchPermissions,
}: SubmitUpdateDriverHandlerProps) => {
  try {
    setLoadingSave(true);

    const equipos = (values?.equipos ?? [])
      .map((t) => {
        const raw = t?.equipoId;
        const selected = Array.isArray(raw) ? raw?.[0] : raw;
        return typeof selected === "number" ? selected : Number(selected);
      })
      .filter((x) => Number.isFinite(x));

    const rawActivo = (values as any)?.activo;
    const activoSelected = Array.isArray(rawActivo) ? rawActivo?.[0] : rawActivo;
    const activo = Boolean(activoSelected);

    if (!equipos.length) return;

    const uniqueEquipos = new Set(equipos);
    if (uniqueEquipos.size !== equipos.length) {
      toaster.create({
        type: "error",
        title: "Hay camiones repetidos. Por favor seleccioná camiones diferentes.",
      });
      return;
    }

    const objToSend: UpdateDriverProps = {
      id,
      activo,
      equipos,
    };

    await updateDriver(objToSend);

    toaster.create({
      type: "success",
      title: "Fletero actualizado con éxito.",
    });

    navigate(AppRoutes.DRIVERS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.DRIVER_EDIT,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo actualizar el fletero.",
      });
    }
  } finally {
    setLoadingSave(false);
  }
};

interface FetchEquiposHandlerProps {
  setLoadingAuxOptions: Dispatch<SetStateAction<boolean>>;
  setEquipmentOptions: Dispatch<SetStateAction<{ label: string; value: number }[]>>;
}

export const fetchEquiposHandler = async ({
  setLoadingAuxOptions,
  setEquipmentOptions,
}: FetchEquiposHandlerProps) => {
  try {
    setLoadingAuxOptions(true);
    const equipos = await getEquipos();

    setEquipmentOptions(
      (equipos ?? []).map((e) => ({
        label: e.nombre,
        value: e.id,
      })),
    );
  } catch {
    setEquipmentOptions([]);
  } finally {
    setLoadingAuxOptions(false);
  }
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<Partial<GetDriversListProps> | undefined>>;
  setPage: Dispatch<SetStateAction<number>>;
  fetchDrivers: (filters?: Partial<GetDriversListProps>, showLoad?: boolean) => Promise<void>;
  event: FieldValues;
}

export const submitFiltersHandler = async ({
  event,
  setOpenFilters,
  setFiltersApplied,
  setCurrentFilters,
  setPage,
  fetchDrivers,
}: SubmitFiltersHandlerProps) => {
  setFiltersApplied(true);

  const filterObj: Partial<GetDriversListProps> = getDriversFilter(event as DLFRProps);

  setCurrentFilters(filterObj);
  if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

  setPage(1);
  await fetchDrivers({ ...filterObj, "P.Pagina": 1 }, true);

  setOpenFilters(false);
};

interface DeleteDriversHandlerProps {
  selection: string[];
  setLoadingDeleteDrivers: Dispatch<SetStateAction<boolean>>;
  setOpenConfirm: Dispatch<SetStateAction<boolean>>;
  fetchDrivers: (filters?: Partial<GetDriversListProps>, showLoad?: boolean) => Promise<void>;
  setSelection: Dispatch<SetStateAction<string[]>>;
}

export const handleDeleteDriversHandler = async ({
  fetchDrivers,
  selection,
  setLoadingDeleteDrivers,
  setOpenConfirm,
  setSelection,
}: DeleteDriversHandlerProps) => {
  try {
    setLoadingDeleteDrivers(true);

    const idsToDelete = selection.map((id) => Number.parseInt(id));

    await deleteDrivers({ Ids: idsToDelete });

    toaster.create({
      title: "Fleteros eliminados con éxito.",
      type: "success",
    });

    setSelection([]);
    setOpenConfirm(false);
    fetchDrivers({}, false);
  } catch (error) {
    const errorMessage = getAxiosErrorMessage(
      error,
      "No se pudieron eliminar los fleteros.",
    );

    toaster.create({
      type: "error",
      title: errorMessage,
    });
  } finally {
    setLoadingDeleteDrivers(false);
  }
};

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event?: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = async ({
  setValue,
  setFiltersApplied,
  submitFilters,
  getValues,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("nombreApellido", "");
  setValue("activo", undefined);
  setValue("equipos", []);

  await submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface FetchActiveTrucksHandlerProps {
  setLoadingTrucks: Dispatch<SetStateAction<boolean>>;
  setTruckOptions: Dispatch<SetStateAction<{ label: string; value: string }[]>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchActiveTrucksHandler = async ({
  setLoadingTrucks,
  setTruckOptions,
  navigate,
  fetchPermissions,
}: FetchActiveTrucksHandlerProps) => {
  try {
    setLoadingTrucks(true);
    const response = await getTrucksList({
      activo: true,
      "P.Pagina": 1,
      "P.Records": 9999,
    });

    const items = response?.listado ?? [];
    setTruckOptions(
      items.map((t: any) => ({
        label: `${t.patente ?? ""} - ${t.nombre ?? ""}`.trim(),
        value: String(t.id),
      })),
    );
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.DRIVER_CREATION,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudieron cargar los camiones activos.",
      });
    }
  } finally {
    setLoadingTrucks(false);
  }
};

export interface GetADOptionsHandlerProps {
  inputValue: string;
}

export const getADOptionsHandler = async ({
  inputValue,
}: GetADOptionsHandlerProps): Promise<Option[]> => {
  const filtered = await getDriversActiveDirectoryUsers({ busqueda: inputValue });
  return (
    filtered?.map((p) => ({
      label: (
        `${p?.displayName ?? ""}`.trim() ||
        `${p?.givenName ?? ""} ${p?.surname ?? ""}`.trim() ||
        String(p.id)
      )
        .concat(p?.mail ? ` - ${p.mail}` : "")
        .trim(),
      value: p.id,
    })) ?? []
  );
};

export interface LoadADOptionsHandlerProps {
  timeoutRef: RefObject<ReturnType<typeof setTimeout> | null>;
  inputValue: string;
  setLoadingADUsers: Dispatch<SetStateAction<boolean>>;
  getADOptions: (inputValue: string) => Promise<Option[]>;
}

export const loadADOptionsHandler = ({
  timeoutRef,
  inputValue,
  setLoadingADUsers,
  getADOptions,
}: LoadADOptionsHandlerProps): Promise<Option[]> => {
  return new Promise((resolve) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(async () => {
      try {
        if (inputValue.trim().length < 3) {
          setLoadingADUsers(false);
          resolve([]);
          return;
        }

        setLoadingADUsers(true);
        const options = await getADOptions(inputValue);
        resolve(options);
      } catch {
        resolve([]);
      } finally {
        setLoadingADUsers(false);
      }
    }, 1000);
  });
};

interface FetchDefaultADUsersHandlerProps {
  setLoadingADUsers: Dispatch<SetStateAction<boolean>>;
  setDefaultADUsers: Dispatch<SetStateAction<ActiveDirectoryDriverUser[]>>;
}

export const fetchDefaultADUsersHandler = async ({
  setLoadingADUsers,
  setDefaultADUsers,
}: FetchDefaultADUsersHandlerProps) => {
  try {
    setLoadingADUsers(true);
    const users = await getDriversActiveDirectoryUsers({});
    setDefaultADUsers(users ?? []);
  } catch {
    setDefaultADUsers([]);
  } finally {
    setLoadingADUsers(false);
  }
};

interface HandleUserSelectionHandlerProps {
  opt: Option | null;
  adUserById: Map<string, ActiveDirectoryDriverUser>;
  setValue: UseFormSetValue<any>;
}

export const handleUserSelectionHandler = async ({
  opt,
  adUserById,
  setValue,
}: HandleUserSelectionHandlerProps) => {
  if (!opt?.value) return;

  const id = String(opt.value);
  let user = adUserById.get(id);

  if (!user) {
    const fetched = await getDriversActiveDirectoryUsers({ busqueda: id });
    user = fetched?.find((u) => String(u.id) === id);
  }

  const fullName =
    `${user?.givenName ?? ""} ${user?.surname ?? ""}`.trim() ||
    (user?.displayName ?? "");

  setValue("nombreApellido", fullName);
  setValue("email", user?.mail ?? "");
};

export interface DriverCreationFormValues {
  usuario?: Option | null;
  nombreApellido?: string;
  email?: string;
  equipos?: { equipoId?: unknown }[];
}

interface SubmitCreateDriverHandlerProps {
  values: DriverCreationFormValues;
  setLoadingCreate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const submitCreateDriverHandler = async ({
  values,
  setLoadingCreate,
  navigate,
  fetchPermissions,
}: SubmitCreateDriverHandlerProps) => {
  try {
    setLoadingCreate(true);

    const usuario = String(values?.usuario?.value ?? "");
    const nombreApellido = String(values?.nombreApellido ?? "");
    const email = String(values?.email ?? "");

    const equipos = (values?.equipos ?? [])
      .map((t) => {
        const raw = t?.equipoId;
        const selected = Array.isArray(raw) ? raw?.[0] : raw;
        return typeof selected === "number" ? selected : Number(selected);
      })
      .filter((x) => Number.isFinite(x));

    if (isEmpty(usuario) || isEmpty(nombreApellido) || !equipos.length) return;

    const uniqueEquipos = new Set(equipos);
    if (uniqueEquipos.size !== equipos.length) {
      toaster.create({
        type: "error",
        title: "Hay camiones repetidos. Por favor seleccioná camiones diferentes.",
      });
      return;
    }

    const objToSend: CreateDriverProps = {
      usuario,
      nombreApellido,
      email,
      equipos,
    };

    await createDriver(objToSend);

    toaster.create({
      type: "success",
      title: "Fletero creado con éxito.",
    });

    navigate(AppRoutes.DRIVERS);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.DRIVER_CREATION,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: "No se pudo crear el fletero.",
      });
    }
  } finally {
    setLoadingCreate(false);
  }
};
